import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import { Button, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import CloseIcon from "@mui/icons-material/Close";
import { Stack } from "@mui/system";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

type Props = {
  sortConfig: {
    field: string | null;
    direction: "asc" | "desc";
  };
  setSortConfig: React.Dispatch<
    React.SetStateAction<{
      field: string | null;
      direction: "asc" | "desc";
    }>
  >;
};

const Puller = styled("div")(({ theme }) => ({
  width: spacing.Spacing["4xl"].$value,
  height: spacing.Spacing.xs.$value,
  backgroundColor: colors.GrayLightMode[300].$value,
  borderRadius: spacing.Radius.xs.$value,
  position: "absolute",
  top: spacing.Spacing.xl.$value,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: colors.GrayLightMode[300].$value,
  }),
}));

export default function BrandsSortBy({ sortConfig, setSortConfig }: Props) {
  const [open, setOpen] = React.useState(false);
  const [tempField, setTempField] = React.useState<string | null>(
    sortConfig.field,
  );

  const [tempDirection, setTempDirection] = React.useState<"asc" | "desc">(
    sortConfig.direction,
  );

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  React.useEffect(() => {
    if (open) {
      setTempField(sortConfig.field);
      setTempDirection(sortConfig.direction);
    }
  }, [open, sortConfig]);

  const options = [
    { label: "Registrations", value: "reg" },
    { label: "Click out", value: "clicks" },
    { label: "FTD", value: "ftd" },
    { label: "FTD %", value: "ftd_perc" },
    { label: "QFTD", value: "qftd" },
    { label: "QFTD %", value: "qftd_perc" },
    { label: "ROI", value: "roi" },
    { label: "CPA Income", value: "cpa_income_usd" },
    { label: "RS Income", value: "revshare_income_usd" },
    { label: "Total Income", value: "total_income_usd" },
    { label: "PV", value: "pv" },
    { label: "EPC", value: "epc" },
    { label: "EPC CPA", value: "epc_cpa" },
    { label: "Brand ID", value: "brand_id" },
  ];

  const ShortList = (
    <Box role="shortBy">
      <Puller />
      <Stack justifyContent={"end"} direction="row" p={1}>
        <IconButton
          aria-label="Logout"
          onClick={toggleDrawer(false)}
          sx={{
            width: spacing.Spacing["3xl"].$value,
            height: spacing.Spacing["3xl"].$value,
            color: colors.GrayLightMode[700].$value,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography
        variant="body1"
        sx={{
          fontSize: typography.Size.Text.sm.$value,
          color: colors.GrayLightMode[700].$value,
        }}
      >
        Sort By
      </Typography>

      <Box sx={{ my: 2 }}>
        {options.map(({ label, value }) => {
          const isSelected = tempField === value;
          const handleClick = () => {
            if (tempField !== value) {
              // New field selected → default desc
              setTempField(value);
              setTempDirection("desc");
            } else {
              // Same field clicked → toggle direction
              setTempDirection((prev) => (prev === "asc" ? "desc" : "asc"));
            }
          };
          return (
            <Button
              key={value}
              onClick={handleClick}
              startIcon={
                isSelected ? (
                  <ArrowUpwardIcon
                    sx={{
                      transform:
                        tempDirection === "desc"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                ) : undefined
              }
              variant={isSelected ? "outlined" : "outlined"}
              color="secondary"
              sx={{
                borderRadius: 2,
                mr: 1,
                mb: 1,
                py: spacing.Spacing.xs.$value,
                px: spacing.Spacing.lg.$value,
                textTransform: "capitalize",
                fontWeight: typography.Font_weight.Regular.$value,
                border: "1px solid" + colors.GrayLightMode[200].$value,
                color: colors.GrayLightMode[700].$value,
                fontSize: typography.Size.Text.sm.$value,

                ...(isSelected && {
                  border: "1px solid" + colors.Brand[600].$value + "!important",
                  boxShadow: "none !important",
                  color: colors.GrayLightMode[900].$value,
                }),
              }}
            >
              {label}
            </Button>
          );
        })}
      </Box>

      <Button
        fullWidth
        variant="contained"
        sx={{
          textTransform: "capitalize",
          boxShadow: "none !important",
          background: colors.Brand[600].$value,
          borderRadius: 2,
        }}
        disabled={!tempField}
        onClick={() => {
          setSortConfig({
            field: tempField,
            direction: tempDirection,
          });
          setOpen(false);
        }}
      >
        Apply
      </Button>
    </Box>
  );

  return (
    <>
      <IconButton
        sx={{
          backgroundColor: colors.Brand[50].$value,
          borderRadius: spacing.Radius.full.$value,
          "&:hover": {
            backgroundColor: colors.Brand[100].$value,
          },
        }}
        color="secondary"
        size="small"
        onClick={toggleDrawer(true)}
        data-tracker="653"
      >
        <FilterListOutlinedIcon
          sx={{ color: colors.GrayLightMode[700].$value }}
        />
      </IconButton>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: spacing.Spacing.xl.$value + "!important",
          },
        }}
      >
        {ShortList}
      </Drawer>
    </>
  );
}
